import { Pointers } from "@opendaw/studio-enums";
import { PlayfieldSampleBox } from "@opendaw/studio-boxes";
import { Option, StringMapping, Terminator, UUID, ValueMapping } from "@opendaw/lib-std";
import { Devices } from "../../../DeviceAdapter";
import { IndexedBoxAdapterCollection } from "../../../IndexedBoxAdapterCollection";
import { ParameterAdapterSet } from "../../../ParameterAdapterSet";
import { AudioFileBoxAdapter } from "../../../audio/AudioFileBoxAdapter";
import { TrackType } from "../../../timeline/TrackType";
import { PlayfieldDeviceBoxAdapter } from "../PlayfieldDeviceBoxAdapter";
export class PlayfieldSampleBoxAdapter {
    class = "device-host";
    accepts = false;
    type = "instrument";
    #terminator = new Terminator();
    #context;
    #box;
    #midiEffects;
    #audioEffects;
    #parametric;
    namedParameter; // let typescript infer the type
    #file = Option.None;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#midiEffects = this.#terminator.own(IndexedBoxAdapterCollection.create(this.#box.midiEffects, box => this.#context.boxAdapters.adapterFor(box, Devices.isMidiEffect), Pointers.MidiEffectHost));
        this.#audioEffects = this.#terminator.own(IndexedBoxAdapterCollection.create(this.#box.audioEffects, box => this.#context.boxAdapters.adapterFor(box, Devices.isAudioEffect), Pointers.AudioEffectHost));
        this.#parametric = this.#terminator.own(new ParameterAdapterSet(this.#context));
        this.namedParameter = this.#wrapParameters(box);
        this.#terminator.own(this.#box.file.catchupAndSubscribe(pointer => {
            this.#file = pointer.targetVertex.map(({ box }) => this.#context.boxAdapters.adapterFor(box, AudioFileBoxAdapter));
            this.#file.unwrapOrNull()?.getOrCreateLoader(); // triggers preloading file if available
        }));
    }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get peakAddress() { return this.#box.address.append(1001); }
    get indexField() { return this.#box.index; }
    get gate() { return this.#box.gate.getValue(); }
    get exclude() { return this.#box.exclude.getValue(); }
    get label() {
        return `${this.device().labelField.getValue()} > ${this.#file.mapOr(file => file.box.fileName.getValue(), "No file")}`;
    }
    get iconField() { return this.#box.icon; }
    get defaultTrackType() { return TrackType.Notes; }
    get acceptsMidiEvents() { return true; }
    get midiEffectsField() { return this.#box.midiEffects; }
    get inputField() { return this.audioUnitBoxAdapter().box.input; }
    get audioEffectsField() { return this.#box.audioEffects; }
    get tracksField() { return this.audioUnitBoxAdapter().box.tracks; }
    get isAudioUnit() { return false; }
    file() { return this.#file; }
    fileUUID() { return this.#box.file.targetAddress.unwrap().uuid; }
    resetParameters() {
        this.#box.mute.reset();
        this.#box.solo.reset();
        this.#box.exclude.reset();
        this.#box.polyphone.reset();
        this.#box.pitch.reset();
        this.#box.attack.reset();
        this.#box.release.reset();
        this.#box.sampleStart.reset();
        this.#box.sampleEnd.reset();
        this.#box.gate.reset();
    }
    copyToIndex(index) {
        PlayfieldSampleBox.create(this.#box.graph, UUID.generate(), box => {
            box.file.refer(this.#box.file.targetVertex.unwrap());
            box.device.refer(this.#box.device.targetVertex.unwrap());
            box.index.setValue(index);
            box.mute.setValue(this.#box.mute.getValue());
            box.solo.setValue(this.#box.solo.getValue());
            box.sampleStart.setValue(this.#box.sampleStart.getValue());
            box.sampleEnd.setValue(this.#box.sampleEnd.getValue());
            box.attack.setValue(this.#box.attack.getValue());
            box.release.setValue(this.#box.release.getValue());
            box.pitch.setValue(this.#box.pitch.getValue());
            box.exclude.setValue(this.#box.exclude.getValue());
            box.gate.setValue(this.#box.gate.getValue());
            // TODO Copy effects?
        });
    }
    get midiEffects() {
        return this.#midiEffects;
    }
    get inputAdapter() {
        return Option.wrap(this);
    }
    get audioEffects() {
        return this.#audioEffects;
    }
    get labelField() { return this.#box.label; }
    get enabledField() { return this.#box.enabled; }
    get minimizedField() { return this.#box.minimized; }
    device() {
        return this.#context.boxAdapters
            .adapterFor(this.#box.device.targetVertex.unwrap().box, PlayfieldDeviceBoxAdapter);
    }
    deviceHost() { return this.device().deviceHost(); }
    audioUnitBoxAdapter() { return this.deviceHost().audioUnitBoxAdapter(); }
    terminate() { this.#terminator.terminate(); }
    #wrapParameters(box) {
        return {
            gate: this.#parametric.createParameter(box.gate, ValueMapping.linearInteger(0, 2), StringMapping.indices("", ["Off", "On", "Loop"]), "Gate"),
            mute: this.#parametric.createParameter(box.mute, ValueMapping.bool, StringMapping.bool, "Mute"),
            solo: this.#parametric.createParameter(box.solo, ValueMapping.bool, StringMapping.bool, "Solo"),
            polyphone: this.#parametric.createParameter(box.polyphone, ValueMapping.bool, StringMapping.bool, "Polyphone"),
            exclude: this.#parametric.createParameter(box.exclude, ValueMapping.bool, StringMapping.bool, "Exclude"),
            pitch: this.#parametric.createParameter(box.pitch, ValueMapping.linear(-1200, 1200), StringMapping.numeric({
                unit: "cents",
                bipolar: true,
                fractionDigits: 0
            }), "Pitch", 0.0),
            sampleStart: this.#parametric.createParameter(box.sampleStart, ValueMapping.unipolar(), StringMapping.percent(), "Start", 0.0),
            sampleEnd: this.#parametric.createParameter(box.sampleEnd, ValueMapping.unipolar(), StringMapping.percent(), "End", 1.0),
            attack: this.#parametric.createParameter(box.attack, ValueMapping.exponential(0.001, 5.0), StringMapping.numeric({
                unit: "s",
                unitPrefix: true,
                fractionDigits: 1
            }), "Attack"),
            release: this.#parametric.createParameter(box.release, ValueMapping.exponential(0.001, 5.0), StringMapping.numeric({
                unit: "s",
                unitPrefix: true,
                fractionDigits: 1
            }), "Release")
        };
    }
}
